import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import type { NftData } from '@mogul/api-client';
import { NftInclude, NftSort, NftStatus } from '@mogul/api-client';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { nftsApi } from '../../../api';
import { useAuth } from '../../../auth';
import styles from './CreatorsDashboard.module.css';

interface Column {
  id: 'id' | 'chainId' | 'contractAddress' | 'updatedAt' | 'name';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left';
}

const columns: Column[] = [
  {
    id: 'id',
    label: 'ID',
    minWidth: 50,
    align: 'left',
  },
  {
    id: 'name',
    label: 'Name',
    minWidth: 200,
    align: 'right',
  },
  {
    id: 'contractAddress',
    label: 'Contract Address On Record',
    minWidth: 450,
    align: 'right',
  },
  {
    id: 'chainId',
    label: 'Chain ID',
    minWidth: 200,
    align: 'right',
  },
  {
    id: 'updatedAt',
    label: 'Last Updated',
    minWidth: 200,
    align: 'right',
  },
];

export const CreatorsDashboard = () => {
  // note type any is a work around for metadata typing, it is listed as String but is actually Object type when
  // returned and causes compilation errors if typed as NftData[]
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [queryStatus] = useState([NftStatus.Draft]);

  const history = useHistory();

  const { user } = useAuth();

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      history.push('/dashboard');
    } else {
      void nftsApi
        .listNfts({
          limit: rowsPerPage,
          offset: rowsPerPage * page,
          sort: NftSort.UpdatedAtDesc,
          status: queryStatus,
          include: [NftInclude.CreatedBy],
        })
        .then(results => {
          setRows(results.data.data);
          setCount(results.data.meta.totalCount);
        });
    }
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    void nftsApi
      .listNfts({
        limit: rowsPerPage,
        offset: rowsPerPage * newPage,
        sort: NftSort.CreatedAtDesc,
        status: queryStatus,
        include: [NftInclude.CreatedBy],
      })
      .then(results => {
        setRows(results.data.data);
        setPage(newPage);
      });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    void nftsApi
      .listNfts({
        limit: +event.target.value,
        offset: 0,
        sort: NftSort.CreatedAtDesc,
        status: queryStatus,
        include: [NftInclude.CreatedBy],
      })
      .then(results => {
        setRows(results.data.data);
        setPage(0);
      });
    setPage(0);
  };

  const openNft = (value: NftData) => {
    history.push(
      '/platform/creators/mint/' + value.contractAddress + '/' + value.id,
    );
  };

  return (
    <div className={styles.creatorDashboardContainer}>
      <h1 className={styles.heading}>Creators' Dashboard</h1>
      <Box className={styles.paper}>
        <TableContainer className={styles.tableContainer}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ width: column.minWidth }}
                    className={styles.tableHeader}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                return (
                  <TableRow
                    className={styles.tableRow}
                    role="checkbox"
                    tabIndex={-1}
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    key={row.id + row.contractAddress}
                    onClick={() => openNft(row)}
                    hover
                  >
                    {columns.map(column => {
                      let value = '';
                      if (column.id === 'updatedAt') {
                        value = row[column.id]
                          .replaceAll(/[TZ]/g, ' ')
                          .replace('.000', '');
                      } else if (column.id === 'name') {
                        if (row['generatedMetadata']) {
                          value = row.generatedMetadata[column.id];
                        }
                      } else {
                        value = row[column.id];
                      }
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          className={styles.tableBody}
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              <TableRow>
                <TablePagination
                  className={styles.tablePaginate}
                  rowsPerPageOptions={[10, 15, 25, 100]}
                  count={count}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
};
